import "rc-slider/assets/index.css";
import "./TimelineWorksheet.scss";

import { format } from "date-fns";
import { cloneDeep } from "lodash-es";
import Slider from "rc-slider";
import { useEffect, useState } from "react";

import type { RevMetaType, RevType } from "../../shared/types/sharedb";
import type { WorksheetType } from "../../shared/types/worksheet";
import { jsonOTType } from "../../shared/worksheets/ot-types/ot-patch";

import { getRevs } from "api/api-worksheets";
import { t } from "i18n/i18n";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId, selectCurrentUserId, userSelectors } from "store/selectors";
import { toastLocalizedOperationError } from "utils/alerts";
import { getUserFullName } from "utils/user-utils";
import { getFakeDoc } from "worksheets/helpers/fake-doc";
import { Worksheet } from "worksheets/views/Worksheet";

type Revision = [WorksheetType, RevMetaType];

export const TimelineWorksheet = ({ docId }: { docId: string }) => {
  const [currentRev, setCurrentRev] = useState(0);
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [dayMarks, setDayMarks] = useState<Record<string, string>>({});
  const [revs, setRevs] = useState<RevType[] | null>(null);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const currentSectionId = useAppSelector(selectCurrentSectionId);

  const users = useAppSelector(userSelectors.selectEntities);

  useEffect(() => {
    if (!currentSectionId) {
      return;
    }

    const process = (res: RevType[]) => {
      setRevs(res);

      const beg = res.shift();
      if (!beg?.create?.data) {
        return;
      }

      const currentRevisions: Revision[] = res.reduce(
        (acc, x) => {
          const result = jsonOTType.type.apply(
            cloneDeep(acc[acc.length - 1][0]),
            x.op,
          ) as WorksheetType;

          return [...acc, [result, x.m]];
        },
        [[beg.create.data, beg.m]],
      );

      let previousDay: number | null = null;
      const currentDayMarks = currentRevisions.reduce(
        (acc, [, meta]: Revision, i) => {
          const day = Math.floor(meta.ts / (1000 * 60 * 60 * 24));
          const dateName = new Date(meta.ts).toDateString().split(" ").slice(1, 3).join(" ");

          if (day !== previousDay) {
            previousDay = day;
            return { ...acc, [i]: dateName };
          }

          return acc;
        },
        {} as Record<number, string>,
      );

      setRevisions(currentRevisions);
      setDayMarks(currentDayMarks);
    };

    getRevs(docId, currentSectionId)
      .then((response) => process(response.data))
      .catch(() => toastLocalizedOperationError("failed_to_retrieve_revisions"));
  }, [docId, currentSectionId]);

  if (!revs) {
    return (
      <div className="mt-4 flex items-center justify-center">{t("common.loading_ellipsis")}</div>
    );
  }

  if (revs.length === 0) {
    return (
      <div className="mt-4 flex items-center justify-center">
        {t("worksheet.timeline.no_revisions")}
      </div>
    );
  }

  const [data, meta] = revisions[currentRev];
  const modifyDate = new Date(meta.ts);

  const timezone = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
    .formatToParts(modifyDate)
    ?.find((part) => part.type === "timeZoneName")?.value;

  const modifyDateString = `${format(modifyDate, "MMM dd, yyyy - hh:mm:ss a")}${timezone ? ` (${timezone})` : ""}`;
  const user = users[meta.userId] || null;
  const username = getUserFullName(user) || t("user_info.anonymous_user");
  const revisionLabel =
    user?.id === currentUserId
      ? t("worksheet.timeline.revision_label_current_user", { date: modifyDateString })
      : t("worksheet.timeline.revision_label", { date: modifyDateString, userName: username });

  const skeletonDocWithHistoricData = getFakeDoc(data);

  window.revs = revs;
  window.currentRev = revs[currentRev + 1];
  window.currentRevData = [data, meta];

  return (
    <div className="flex w-full flex-col items-center">
      {/* TODO: Preferably this slider would be pinned to the top of the page, and sliding
      it would scroll the page to the relevant change, which would require inspecting each
      op and jumping to the corresponding block component that renders it. */}
      <div className="extended-page-block-width body px-5 pt-5">
        <h2 className="h2">{t("worksheet.timeline.title")}</h2>

        <div>{t("worksheet.timeline.description")}</div>

        <div className="body mb-2 mt-5">{revisionLabel}</div>

        <div className="mx-4 pb-4">
          <Slider
            marks={dayMarks}
            max={revisions.length - 1}
            min={0}
            value={currentRev}
            onChange={(e) => setCurrentRev(e)}
          />
        </div>

        <div className="mb-2 mt-5 w-full border-b border-black-tint-70" />
      </div>

      <Worksheet doc={skeletonDocWithHistoricData} />
    </div>
  );
};
